import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  CircularProgress,
  Snackbar,
  Alert,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { styled, useTheme } from '@mui/system'; // Correctly import useTheme from @mui/system
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import './createUserpage.css'; // Link the CSS file

// Assuming this is your base URL configuration
import { baseUrl } from '../utils/config';

// Image for the right side
import createUserImage from '../assets/images/adduser.png';

// Styled component for the form container
const FormContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  maxWidth: 600,
  width: '100%',
  [theme.breakpoints.down('md')]: { // Adjust padding and gap for smaller screens
    padding: theme.spacing(2),
    gap: theme.spacing(2),
  },
  [theme.breakpoints.down('sm')]: { // Further adjust for very small screens
    padding: theme.spacing(1.5),
    gap: theme.spacing(1.5),
  },
}));

// Styled component for the right-side image
const RightImageContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 1,
  padding: theme.spacing(3),
  [theme.breakpoints.down('md')]: {
    height: '250px', // Fixed height on small screens when stacked (though it will be hidden)
    marginTop: theme.spacing(4),
    display: 'none', // Explicitly hide on medium and smaller screens
  },
}));

const CreateUserScreen = () => {
  const theme = useTheme(); // Access theme here
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: '',
    confirmPassword: '',
    designation: '',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [validationErrors, setValidationErrors] = useState({});

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const designations = [
    'Admin',
    'Manager',
    'Supervisor',
    'Employee',
    'Reviewer',
    'Approver'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword((prev) => !prev);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const validateForm = () => {
    let errors = {};
    let isValid = true;

    if (!formData.name.trim()) {
      errors.name = 'Name is required';
      isValid = false;
    }
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
      isValid = false;
    }
    if (!formData.password.trim()) {
      errors.password = 'Password is required';
      isValid = false;
    } else if (formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters long';
      isValid = false;
    }
    if (!formData.confirmPassword.trim()) {
      errors.confirmPassword = 'Confirm Password is required';
      isValid = false;
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
      isValid = false;
    }
    if (!formData.designation) {
      errors.designation = 'Designation is required';
      isValid = false;
    }

    setValidationErrors(errors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      _showSnackbar('Please correct the errors in the form.', 'error');
      return;
    }

    setIsLoading(true);
    try {
      const { confirmPassword, ...dataToSend } = formData;
      const response = await fetch(`${baseUrl}/api/users/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dataToSend),
      });

      const responseData = await response.json();

      if (response.ok) {
        _showSnackbar(responseData.message || 'User created successfully!', 'success');
        setFormData({
          email: '',
          name: '',
          password: '',
          confirmPassword: '',
          designation: '',
        });
        setValidationErrors({});
      } else {
        if (response.status === 409) {
          _showSnackbar(responseData.message || 'Email already exists.', 'warning');
          setValidationErrors(prev => ({ ...prev, email: responseData.message || 'Email already exists' }));
        } else {
          _showSnackbar(responseData.message || 'Failed to create user.', 'error');
        }
        console.error('Error creating user:', responseData);
      }
    } catch (error) {
      _showSnackbar('Failed to connect to the server. Please try again.', 'error');
      console.error('Network or server error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const _showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  // Helper function to render label and field with improved responsiveness
  const renderRowLabelField = (label, fieldComponent, fieldName) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: { xs: 'flex-start', sm: 'center' }, // Align items to start on xs (for stacking), center on sm+
        mb: 2,
        flexDirection: { xs: 'column', sm: 'row' }, // Stack on xs, row on sm and up
        width: '100%', // Ensure the box takes full width
      }}
    >
      <Typography
        variant="body1"
        sx={{
          fontWeight: 'bold',
          width: { xs: '100%', sm: '150px' }, // Full width label on xs, fixed on sm+
          flexShrink: 0,
          whiteSpace: { xs: 'normal', sm: 'nowrap' }, // Allow wrapping on xs, no wrap on sm+
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          mr: { xs: 0, sm: 2 }, // No right margin on xs, 2 on sm+
          mb: { xs: 1, sm: 0 }, // Add bottom margin on xs for spacing
          textAlign: { xs: 'left', sm: 'left' }, // Align text left regardless of screen size
          fontSize: { xs: '0.9rem', sm: '1rem' } // Adjust font size for labels
        }}
      >
        {label}:
      </Typography>
      <Box sx={{ flexGrow: 1, width: { xs: '100%', sm: 'auto' } }}> {/* Ensure field takes full width on xs */}
        {fieldComponent}
        {validationErrors[fieldName] && (
          <FormHelperText error>{validationErrors[fieldName]}</FormHelperText>
        )}
      </Box>
    </Box>
  );

  return (
    <div className="createuser-page-container">
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        minHeight: 'calc(100vh - 64px)', // Adjust for AppBar height if present
        backgroundColor: '#f0f2f5',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(4),
        p: { xs: 2, md: 3 }, // Overall padding for the screen, less on xs
      }}
    >
      {/* Left Column: Form */}
      <Box
        sx={{
          flex: { xs: 'none', md: 3 },
          minWidth: { xs: '100%', md: 'auto' },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start', // Align content to the left
          justifyContent: 'center',
          p: { xs: 0, md: 8 }, // No extra padding on xs, FormContainer handles it
          maxWidth: { md: '700px' },
          width: '100%',
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{
            mb: { xs: 2, md: 4 }, // Adjust margin bottom
            color: '#4A148C',
            fontWeight: 'bold',
            fontFamily: 'Poppins, sans-serif',
            textAlign: { xs: 'center', md: 'left' }, // Center on xs, left on md+
            width: '100%', // Take full width to center text
          }}
        >
          Create New User
        </Typography>

        <FormContainer elevation={3} component="form" onSubmit={handleSubmit}>
          {renderRowLabelField(
            'Name',
            <TextField
              name="name"
              label="User Name"
              variant="outlined"
              fullWidth
              size="small"
              value={formData.name}
              onChange={handleChange}
              error={!!validationErrors.name}
            />,
            'name'
          )}

          {renderRowLabelField(
            'Email',
            <TextField
              name="email"
              label="Email Address"
              variant="outlined"
              fullWidth
              size="small"
              type="email"
              value={formData.email}
              onChange={handleChange}
              error={!!validationErrors.email}
            />,
            'email'
          )}

          {/* Password Field with Eye Button */}
          {renderRowLabelField(
            'Password',
            <TextField
              name="password"
              label="Password"
              variant="outlined"
              fullWidth
              size="small"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              error={!!validationErrors.password}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />,
            'password'
          )}

          {/* Confirm Password Field with Eye Button */}
          {renderRowLabelField(
            'Confirm Password',
            <TextField
              name="confirmPassword"
              label="Confirm Password"
              variant="outlined"
              fullWidth
              size="small"
              type={showConfirmPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={handleChange}
              error={!!validationErrors.confirmPassword}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle confirm password visibility"
                      onClick={handleClickShowConfirmPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />,
            'confirmPassword'
          )}

          {renderRowLabelField(
            'Designation',
            <FormControl fullWidth size="small" error={!!validationErrors.designation}>
              <InputLabel>Designation</InputLabel>
              <Select
                name="designation"
                value={formData.designation}
                label="Designation"
                onChange={handleChange}
                error={!!validationErrors.designation}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {designations.map((desig) => (
                  <MenuItem key={desig} value={desig}>
                    {desig}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>,
            'designation'
          )}

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-start' }}>
            <Button
              type="submit"
              variant="contained"
              disabled={isLoading}
              sx={{
                padding: '14px 32px',
                borderRadius: '10px',
                boxShadow: '0 5px 15px rgba(74, 20, 140, 0.4)',
                background: 'linear-gradient(to top left, #4A148C, #1A237E, #0D47A1)',
                color: 'white',
                fontSize: '18px',
                textTransform: 'none',
                '&:hover': {
                  background: 'linear-gradient(to bottom right, #0D47A1, #1A237E, #4A148C)',
                },
                width: { xs: '100%', sm: 'auto' }, // Full width on xs, auto on sm+
              }}
            >
              {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Create User'}
            </Button>
          </Box>
        </FormContainer>
      </Box>

      {/* Right Column: Image - This will be hidden on md and smaller by RightImageContainer's styled component */}
      <RightImageContainer sx={{ flex: { xs: 'none', md: 2 } }}>
        <Box
          component="img"
          src={createUserImage}
          alt="Create User Illustration"
          sx={{
            maxHeight: '70vh',
            width: 'auto',
            objectFit: 'contain',
          }}
        />
      </RightImageContainer>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box></div>
  );
};

export default CreateUserScreen;